import { useEffect, useState } from 'react';

import { Box, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import logo from '../images/class-logo-full-color-rgb-313px@72ppi.png';
import { getUrls } from '../utils/url';
import MicrosoftLoginButton from '../views/Login/MicrosoftLoginButton';

const StyledHeader = styled.div`
    margin: 10px;
    display: flex;
    align-items: flex-end;
`;

const StyledLogo = styled.img`
    width: 151px;
`;

const StyledTitle = styled(Typography)`
    margin-left: 26px;

    font-family: Lato;
    font-size: 20px;
    font-style: normal;
    font-weight: bold;
    letter-spacing: 0.15px;
    text-align: center;
`;

const StyledRoot = styled(Box)`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    padding-left: 10px;
    padding-right: 10px;
`;

const RequireLoginText = styled(Typography)`
    margin-top: 30px;
    margin-bottom: 30px;
    text-align: center;
`;

const StyledContentDiv = styled(Stack)`
    width: max-content;
    flex-direction: column;
    align-items: center;
`;

const SkipButton = styled(Button)`
    padding: 10px;
    width: 100%;
    text-align: center;
`;

const MicrosoftAuthRedirect = () => {
    const { ready, t } = useTranslation('microsoft_auth');

    const search = new URLSearchParams(window.location.search);
    const title = search.get('title');
    const classURL = search.get('classURL');

    const [msWindow, setMsWindow] = useState<Window | null>(null);

    useEffect(() => {
        if (!msWindow) {
            console.debug('No MS login window');
            return;
        }

        const origin = window.location.origin;
        console.info('Listening for messages to', origin);
        window.addEventListener('message', (event) => {
            console.debug('Received event', event);
            if (event.origin === origin) {
                if (event.data.success && classURL) {
                    window.location.href = classURL;
                } else {
                    window.location.reload();
                }
            }
        });

        const interval = setInterval(() => {
            if (msWindow.closed) {
                clearInterval(interval);
                window.location.reload();
            }
        }, 500);
    }, [msWindow, classURL]);

    const redirect = (url: string) => {
        setMsWindow(window.open(url, '_blank', 'popup=true,top=100,left=100,width=800,height=600'));
    };

    const handleSkipRedirect = () => {
        if (classURL) {
            window.location.replace(classURL);
        }
    };

    const Header = () => (
        <StyledHeader>
            <StyledLogo src={logo} />
            <StyledTitle>{title}</StyledTitle>
        </StyledHeader>
    );

    if (ready) {
        return (
            <StyledRoot>
                <Header />
                <RequireLoginText>{t('require-login')}</RequireLoginText>
                <StyledContentDiv>
                    <MicrosoftLoginButton
                        state={{
                            target: `${getUrls().vanityUrl}/react/microsoft-auth-success?${search}`,
                            email: search.get('email') ?? undefined
                        }}
                        redirect={redirect}
                    />

                    {classURL && (
                        <SkipButton disabled={msWindow !== null} onClick={() => handleSkipRedirect()}>
                            {t('skip-login')}
                        </SkipButton>
                    )}
                </StyledContentDiv>
            </StyledRoot>
        );
    }

    return <></>;
};

export default MicrosoftAuthRedirect;
